import { Tooltip } from '@ipfabric/ui-components/Tooltip/Tooltip';
import { copyToClipboard } from '@ipfabric/ui-components/utils/copyToClipboard';
import { graphOverlayToRequest } from '@ipfabric/utils';
import { prop } from '@ipfabric/utils/fp/prop';
import { Button } from 'components/Button/Button';
import { ModalRightDrawer } from 'components/ModalRightDrawer/ModalRightDrawer';
import { useLocale } from 'containers/App/LocaleContext';
import { useMemo } from 'react';
import type { FC } from 'react';
import ReactMarkdown from 'react-markdown';
import { apiEndpointToFullUrl } from 'utils/apiEndpointToFullUrl';
import { useNotifications } from 'utils/hooks/useNotifications';
import { useGraphContextSelectors } from '../GraphContext';
import { selectAppliedPreset } from '../GraphSidePanel/selectors/selectAppliedPreset';
import { createGraphApiRequest } from '../helpers/createGraphApiRequest';
import { getNodePositions } from '../helpers/getNodePositions';
import type { GraphDocs } from './GraphApiDocumentation';

interface Props {
  docs: GraphDocs | null;
  onClose: () => void;
}

export const GraphApiDocumentationModal: FC<Props> = (props) => {
  const { showNotification } = useNotifications();
  const ctxProps = useGraphContextSelectors({
    appliedPreset: selectAppliedPreset,
    overlay: prop('overlay'),
    graphMetadata: prop('graphMetadata'),
    nodes: prop('graph', 'nodes'),
    collapsedNodeGroups: prop('graph', 'collapsedNodeGroups'),
    snapshotId: prop('snapshotId'),
    attributeFilters: prop('attributeFilters'),
  });
  const {
    canvasGraph: {
      graphToolbar: { apiDocumentation: msgApiDocumentation },
    },
  } = useLocale();

  const payload = useMemo(() => {
    if (ctxProps.graphMetadata) {
      const positions = getNodePositions(
        ctxProps.nodes,
        ctxProps.collapsedNodeGroups,
      );

      const request = createGraphApiRequest({
        overlay: ctxProps.overlay
          ? graphOverlayToRequest(ctxProps.overlay)
          : undefined,
        parameters: ctxProps.graphMetadata.parameters,
        positions,
        snapshotId: ctxProps.snapshotId,
        settings: ctxProps.appliedPreset,
        attributeFilters: ctxProps.attributeFilters,
      });

      return JSON.stringify(request.data, null, 2);
    }
    return null;
  }, [
    ctxProps.overlay,
    ctxProps.attributeFilters,
    ctxProps.graphMetadata,
    ctxProps.nodes,
    ctxProps.collapsedNodeGroups,
    ctxProps.snapshotId,
    ctxProps.appliedPreset,
  ]);

  const handleCopyClick = (text: string) => {
    copyToClipboard(text);
    showNotification({
      type: 'success',
      message: msgApiDocumentation.copyToClipboardSuccess,
    });
  };

  if (props.docs === null) {
    return null;
  }

  const dataUrl = apiEndpointToFullUrl('/graphs');

  const pngUrl = `${dataUrl}/png`;
  const svgUrl = `${dataUrl}/svg`;

  const renderUrlCopier = (url: string, name: string) => (
    <p>
      <strong>{msgApiDocumentation.url}</strong>
      {` (${name})`}
      {': '}
      <code>{url}</code>
      <Tooltip content={msgApiDocumentation.copyToClipboard}>
        <Button
          variant='tertiary'
          icon='fa-copy'
          size='xxs'
          onClick={() => {
            handleCopyClick(url);
          }}
          className='ml-1'
          type='button'
        />
      </Tooltip>
    </p>
  );

  return (
    <ModalRightDrawer
      onClose={() => {
        props.onClose();
      }}
      title={msgApiDocumentation.title}
    >
      {renderUrlCopier(dataUrl, msgApiDocumentation.data)}
      {renderUrlCopier(svgUrl, msgApiDocumentation.svg)}
      {renderUrlCopier(pngUrl, msgApiDocumentation.png)}
      <ReactMarkdown>{props.docs?.headers}</ReactMarkdown>
      <div className='mb-4 mt-8 flex items-center'>
        <h3 className='m-0'>{msgApiDocumentation.requestPayload}</h3>
        {payload && (
          <Tooltip content={msgApiDocumentation.copyToClipboard}>
            <Button
              variant='tertiary'
              icon='fa-copy'
              size='xxs'
              onClick={() => {
                handleCopyClick(payload);
              }}
              className='ml-1'
              type='button'
            />
          </Tooltip>
        )}
      </div>
      {payload ? (
        <pre>{payload}</pre>
      ) : (
        <p className='ml-6 italic'>{msgApiDocumentation.emptyPayload}</p>
      )}
      <ReactMarkdown>{props.docs?.payloadBase}</ReactMarkdown>
    </ModalRightDrawer>
  );
};
